import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { EtsyButton } from "@/components/BuyButtons";
import { MENS_JOURNAL } from "@/data/site";

const STEPS = [
  {
    title: "Pick your editions",
    body: "Digital, printed, or one of each—the bundle will pair the two journals however works best for your family.",
  },
  {
    title: "Save with bundle pricing",
    body: "Bundling both editions together will come with a discount when we roll out bundle pricing. Stay tuned.",
  },
  {
    title: "One for you, one for them",
    body: "Keep your own care organized and give the person you look after a journal of their own—two corners, one mission.",
  },
];

export default function Bundle() {
  return (
    <main data-testid="page-bundle" className="pt-32 sm:pt-40 lg:pt-44">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow tone="blush">Coming at launch</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              className="mt-7 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl lg:text-6xl"
              data-testid="bundle-headline"
            >
              The Caregiver Bundle.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-soft sm:text-lg" data-testid="bundle-sub">
              Two journals, one mission: pair the Original and Masculine editions of the In Your Corner
              Healthcare Journal, so you and the person you care for each have a corner of your own.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <Reveal className="h-full">
              <article data-testid="bundle-original" className="h-full rounded-3xl border border-ink/5 bg-white p-7 sm:p-8">
                <div className="overflow-hidden rounded-2xl shadow-lg shadow-lav-soft/60">
                  <img
                    src="/journal-cover.png"
                    alt="In Your Corner Healthcare Journal — Original Edition cover"
                    className="aspect-square w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-display text-xl font-semibold text-ink">Original Edition</h2>
                  <span className="rounded-full bg-mint-soft px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-mint-deep">
                    Available now
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                  $9.99 digital fillable & printable PDF on Etsy · $15.59 printed (30 pages) on Amazon.
                </p>
              </article>
            </Reveal>
            <Reveal delay={0.07} className="h-full">
              <article data-testid="bundle-masculine" className="h-full rounded-3xl border border-dashed border-lav/50 bg-white/60 p-7 sm:p-8">
                <div className="overflow-hidden rounded-2xl shadow-lg shadow-lav-soft/60">
                  <img
                    src={MENS_JOURNAL.cover_image}
                    alt="In Your Corner Healthcare Mini Patient Healthcare Journal — Men's Edition"
                    className="block h-auto w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-display text-xl font-semibold text-ink">Men's Edition</h2>
                  <span className="rounded-full bg-mint-soft px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-mint-deep">
                    Available now
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                  {MENS_JOURNAL.tagline} Now on Etsy—printable & fillable PDF, 14 pages, instant download. Pair
                  the two editions for you and the man you care about.
                </p>
                <div className="mt-4 flex items-end gap-2" data-testid="bundle-mens-price">
                  <span className="font-display text-2xl font-semibold text-ink">{MENS_JOURNAL.price_label}</span>
                  <span className="pb-0.5 text-xs text-ink-faint">{MENS_JOURNAL.price_note}</span>
                </div>
                <div className="mt-4">
                  <EtsyButton href={MENS_JOURNAL.buy_etsy_url} testId="bundle-mens-buy-etsy" className="!px-6 !py-3 text-sm" />
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="bundle-how-headline">
              How the bundle will work
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06} className="h-full">
                <article data-testid={`bundle-step-${i + 1}`} className="h-full rounded-3xl bg-lav-soft p-7">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lav-deep">
                    <HeartHandshake className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-lav-deep">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-soft">{s.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-[2.5rem] bg-ink px-6 py-12 text-center sm:px-12" data-testid="bundle-cta-band">
              <h2 className="font-display text-2xl font-semibold text-cream sm:text-3xl" data-testid="bundle-cta-headline">
                Want first dibs when the bundle goes live?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-cream/70 sm:text-base">
                Send a quick note and we'll email you the moment the Caregiver Bundle is ready. Meanwhile, the
                Original Edition is available today.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  data-testid="bundle-cta-notify"
                  className="group inline-flex items-center gap-2 rounded-full bg-cream px-8 py-4 font-bold text-ink transition-all duration-300 hover:bg-blush-soft hover:text-blush-deep"
                >
                  Tell me when it's live
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/shop"
                  data-testid="bundle-cta-shop"
                  className="group inline-flex items-center gap-2 rounded-full border border-cream/30 px-8 py-4 font-bold text-cream transition-all duration-300 hover:bg-cream/10"
                >
                  Shop the Original
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
